import React, { useState } from "react"
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Collapse,
  Badge,
  Alert,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap"

const CPSStockNotifications = ({ entry, myUser, statusIndex, reload }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [newOrder, setNewOrder] = useState({
    quantity: "",
    deliveryDate: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Determine if this step is active based on entry status
  const isActive = entry?.status === "Stock Management"
  const isCompleted = ["Completed"].includes(entry?.status)

  // Demo stock levels and notifications
  const demoStock = {
    rawStock: 850,
    minLevel: entry?.safetyStockMin || 100,
    maxLevel: entry?.safetyStockMax || 500,
    finishedStock: 400,
  }

  // Simple stock status flags based on demo stock
  const isLowStock = demoStock.finishedStock < demoStock.minLevel
  const isOverStock = demoStock.finishedStock > demoStock.maxLevel

  // Demo notifications
  const demoNotifications = [
    {
      id: 1,
      type: "stock_update",
      message: "Stock levels updated by CPS Lab. Raw: 850 pcs",
      timestamp: "2025-01-19 15:30",
      status: "completed",
      actionRequired: false,
    },
  ]

  const handleNewOrder = async e => {
    e.preventDefault()
    if (!newOrder.quantity || !newOrder.deliveryDate) return

    setIsSubmitting(true)

    setTimeout(() => {
      setIsSubmitting(false)
      setNewOrder({ quantity: "", deliveryDate: "" })
      setIsOpen(false)
      alert("Reorder request submitted!")
    }, 1500)
  }

  const handleAcknowledgeNotification = notificationId => {
    alert(`Notification ${notificationId} acknowledged`)
  }

  const getNotificationIcon = type => {
    switch (type) {
      case "low_stock":
        return "bx bx-error-circle text-danger"
      case "over_stock":
        return "bx bx-warning text-warning"
      case "stock_update":
        return "bx bx-info-circle text-info"
      default:
        return "bx bx-bell"
    }
  }

  const getNotificationColor = type => {
    switch (type) {
      case "low_stock":
        return "danger"
      case "over_stock":
        return "warning"
      case "stock_update":
        return "info"
      default:
        return "secondary"
    }
  }

  return (
    <li className={isActive ? "active" : ""}>
      <div className="timeline-badge">
        <i
          className={`bx bx-bell ${
            isCompleted
              ? "text-success"
              : isActive
              ? "text-primary"
              : "text-muted"
          } font-size-16`}
        ></i>
      </div>
      <div className="timeline-content">
        <div>
          <h5 className="font-size-15 mb-1">
            Stock Level Notifications
            <Badge
              color={
                isLowStock ? "danger" : isOverStock ? "warning" : "success"
              }
              className="ms-2"
            >
              {isLowStock ? "Low Stock" : isOverStock ? "Over Stock" : "OK"}
            </Badge>
          </h5>
          <p className="mb-2 text-muted">
            {isActive
              ? "Automatic stock monitoring with email notifications"
              : "Stock notifications not yet active"}
          </p>

          {isActive && (
            <div className="mb-3">
              {/* Stock Status Summary Card */}
              <Card className="mb-3">
                <CardBody className="p-3">
                  <Row>
                    <Col md="3">
                      <div className="text-center p-2 border rounded">
                        <h6 className="mb-1">Raw Materials</h6>
                        <h5 className="text-primary mb-0">
                          {demoStock.rawStock.toLocaleString()} pcs
                        </h5>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="text-center p-2 border rounded">
                        <h6 className="mb-1">Finished Products</h6>
                        <h5
                          className={
                            isLowStock ? "text-danger" : "text-success"
                          }
                        >
                          {demoStock.finishedStock.toLocaleString()} pcs
                        </h5>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="text-center p-2 border rounded">
                        <h6 className="mb-1">Min Level</h6>
                        <h5 className="text-info mb-0">
                          {demoStock.minLevel.toLocaleString()} pcs
                        </h5>
                      </div>
                    </Col>
                    <Col md="3">
                      <div className="text-center p-2 border rounded">
                        <h6 className="mb-1">Max Level</h6>
                        <h5 className="text-warning mb-0">
                          {demoStock.maxLevel.toLocaleString()} pcs
                        </h5>
                      </div>
                    </Col>
                  </Row>

                  {isLowStock && (
                    <Alert color="danger" className="mt-3 mb-0">
                      <i className="bx bx-error-circle me-1"></i>
                      <strong>Low Stock Alert:</strong> Finished stock is below
                      minimum level. Both parties will receive email
                      notifications.
                    </Alert>
                  )}

                  {isOverStock && (
                    <Alert color="warning" className="mt-3 mb-0">
                      <i className="bx bx-warning me-1"></i>
                      <strong>Over Stock Alert:</strong> Finished stock exceeds
                      maximum level.
                    </Alert>
                  )}
                </CardBody>
              </Card>

              {/* Notifications Card */}
              <Card className="mb-3">
                <CardBody className="p-0">
                  <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <h6 className="mb-0">Recent Notifications</h6>
                    <Badge color="info" size="sm">
                      {demoNotifications.length} Notifications
                    </Badge>
                  </div>

                  <div className="p-3">
                    {demoNotifications.map((notification, index) => (
                      <div
                        key={notification.id}
                        className="d-flex align-items-start mb-3"
                      >
                        <div className="me-3">
                          <i
                            className={`${getNotificationIcon(
                              notification.type
                            )} font-size-18`}
                          ></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-1">{notification.message}</p>
                              <small className="text-muted">
                                {new Date(
                                  notification.timestamp
                                ).toLocaleString()}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Badge
                                color={getNotificationColor(notification.type)}
                                size="sm"
                              >
                                {notification.status}
                              </Badge>
                              {notification.actionRequired && (
                                <Button
                                  color="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleAcknowledgeNotification(
                                      notification.id
                                    )
                                  }
                                >
                                  Acknowledge
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>

              {/* Manual Reorder Section */}
              {isLowStock && (
                <Card className="mb-3">
                  <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Manual Reorder Request</h6>
                      <Button
                        color="info"
                        size="sm"
                        onClick={() => setIsOpen(!isOpen)}
                      >
                        <i className="bx bx-plus me-1"></i>
                        Request Reorder
                      </Button>
                    </div>

                    <Collapse isOpen={isOpen}>
                      <div className="mt-3 p-3 border rounded">
                        <h6 className="mb-3">Create Reorder Request</h6>
                        <Form onSubmit={handleNewOrder}>
                          <Row>
                            <Col md="6">
                              <FormGroup>
                                <Label size="sm">Quantity Needed</Label>
                                <Input
                                  type="number"
                                  size="sm"
                                  value={newOrder.quantity}
                                  onChange={e =>
                                    setNewOrder({
                                      ...newOrder,
                                      quantity: e.target.value,
                                    })
                                  }
                                  placeholder="Enter quantity"
                                  disabled={isSubmitting}
                                  min="1"
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label size="sm">Desired Delivery Date</Label>
                                <Input
                                  type="date"
                                  size="sm"
                                  value={newOrder.deliveryDate}
                                  onChange={e =>
                                    setNewOrder({
                                      ...newOrder,
                                      deliveryDate: e.target.value,
                                    })
                                  }
                                  disabled={isSubmitting}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <div className="d-flex gap-2 mt-2">
                            <Button
                              type="submit"
                              color="primary"
                              size="sm"
                              disabled={
                                !newOrder.quantity ||
                                !newOrder.deliveryDate ||
                                isSubmitting
                              }
                            >
                              {isSubmitting
                                ? "Submitting..."
                                : "Submit Request"}
                            </Button>
                            <Button
                              type="button"
                              color="secondary"
                              size="sm"
                              onClick={() => setIsOpen(false)}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Form>
                      </div>
                    </Collapse>
                  </CardBody>
                </Card>
              )}
            </div>
          )}

          <p className="mb-0 mt-2">
            <i className="bx bx-time-five font-size-13 align-middle me-1"></i>
            <span className="font-size-13">
              {isActive
                ? "Stock monitoring active with notifications"
                : "Not started"}
            </span>
          </p>
        </div>
      </div>
    </li>
  )
}

export default CPSStockNotifications
